import * as React from 'react';
import { ShareableLocation, LocationId, ClientId, t } from '@deltasierra/shared';
import { NestedCheckListDataStructure, CheckListItemStructure, NestedCheckList } from '../CheckList/CheckList';
import { Input } from '../Input';

export type MultipleLocationPickerProps = {
    disabledId: LocationId;
    locations: ShareableLocation[];
    selectedLocationIds: LocationId[];
    onChange: (ids: ShareableLocation[], checked: boolean) => void;
};

const initLocation = (
    location: ShareableLocation,
    selectedIds: LocationId[],
    disabledId: LocationId,
): CheckListItemStructure<WithNumCollections> => {
    const asterisk = location.numCollections >= 20 ? ' *' : '';
    const checked = selectedIds.find(id => id === location.locationId) !== undefined;
    const disabled = disabledId === location.locationId || (location.numCollections >= 20 && !checked);
    const title = `${location.locationTitle} ${asterisk}`;
    return {
        checked,
        disabled,
        id: location.locationId,
        numCollections: location.numCollections,
        title,
    };
};

type WithNumCollections = {
    numCollections: number;
};

const initClient = (
    location: ShareableLocation,
    selectedIds: LocationId[],
    disabledId: LocationId,
): NestedCheckListDataStructure => {
    const newLocation = initLocation(location, selectedIds, disabledId);
    return {
        checked: false,
        id: location.clientId,
        nestedItems: [newLocation],
        title: location.clientTitle,
    };
};

const checkAllTheSame = (locationList: CheckListItemStructure[], disabledId: LocationId): boolean[] => {
    const filteredLocations = locationList.filter(loc => !loc.disabled && loc.id !== disabledId);
    if (filteredLocations.length > 0) {
        const checked = filteredLocations[0].checked;
        // Check to see if we need to check the client as well
        const allTheSameChecked = filteredLocations.reduce(
            (same, location) => location.checked === checked && same,
            true,
        );
        if (allTheSameChecked) {
            return [true, checked];
        }
    }
    return [false];
};

const checkAllDisabled = (locationList: CheckListItemStructure[]) =>
    locationList.filter(loc => loc.disabled).length === locationList.length;

const checkAndDisableClient = (client: NestedCheckListDataStructure, disabledId: LocationId) => {
    // Check the client if all the locations are checked
    const [allTheSame, checked] = checkAllTheSame(client.nestedItems, disabledId);
    const checkedBecauseOnlyDisabledId = client.nestedItems.length === 1 && client.nestedItems[0].id === disabledId;
    client.disabled = checkAllDisabled(client.nestedItems);
    if (checkedBecauseOnlyDisabledId) {
        client.checked = true;
    } else if (allTheSame) {
        client.checked = checked;
    }
};

const searchMatch = (searchTerm: string, location: ShareableLocation) =>
    location.clientTitle.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase()) ||
    location.locationTitle.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase());

const MultipleLocationPicker: React.FC<MultipleLocationPickerProps> = ({
    disabledId,
    locations,
    onChange,
    selectedLocationIds,
}) => {
    const [clientLocationState, setClientLocationState] = React.useState<NestedCheckListDataStructure[]>([]);
    const [searchTerm, setSearchTerm] = React.useState<string>('');

    React.useEffect(() => {
        // When locations changes, rebuild the data structure
        const clientList: NestedCheckListDataStructure[] = [];
        /*
         * Sort locations by client, then filter by search term
         * UPDATE: Don't sort anymore, just filter. Items come in pre-sorted by client, then location name
         */
        const sortedLocations = locations.filter(loc => searchMatch(searchTerm, loc));

        let lastClient: NestedCheckListDataStructure | undefined;
        if (sortedLocations.length > 0) {
            sortedLocations.forEach(location => {
                // Make a location to add to the list, and check its box if necessary
                const locationToPush = initLocation(location, selectedLocationIds, disabledId);

                // Whether it's our first location, or we reach a new client...
                if (!lastClient || ClientId.from(lastClient.id) !== location.clientId) {
                    // If we reached a new client
                    if (lastClient !== undefined) {
                        checkAndDisableClient(lastClient, disabledId);
                    }
                    // Init a new client and add to the structure
                    lastClient = initClient(location, selectedLocationIds, disabledId);
                    clientList.push(lastClient);
                } else {
                    // Else just add the current location to the current client
                    lastClient.nestedItems.push(locationToPush);
                }
            });
            lastClient = clientList[clientList.length - 1];
            checkAndDisableClient(lastClient, disabledId);
        }
        setClientLocationState(clientList);
    }, [disabledId, locations, searchTerm, selectedLocationIds]);

    const onCheck = React.useCallback(
        (id: number, checked: boolean, isClient: boolean) => {
            if (isClient) {
                const client = clientLocationState.find(aClient => aClient.id === id);
                if (client) {
                    onChange(
                        client.nestedItems
                            .filter(loc => !loc.disabled && loc.id !== disabledId && loc.checked !== checked)
                            .map(loc => ({
                                clientId: ClientId.from(client.id),
                                clientTitle: client.title,
                                locationId: LocationId.from(loc.id),
                                locationTitle: loc.title,
                                numCollections: 0,
                            })),
                        checked,
                    );
                }
            } else {
                const location = locations.find(loc => loc.locationId === id);
                if (location) {
                    onChange([location], checked);
                }
            }
        },
        [clientLocationState, disabledId, locations, onChange],
    );

    return (
        <div className="mult-location-picker col-sm-10">
            <div style={{ float: 'left' }}>
                <div
                    className="well"
                    style={{
                        background: 'white',
                        height: 200,
                        overflowX: 'hidden',
                        overflowY: 'auto',
                        scrollbarWidth: 'none',
                        width: 200,
                    }}
                >
                    <Input
                        clearable
                        placeholder={`${t('COMMON.FILTER')}...`}
                        style={{ width: '100%' }}
                        onTermChange={setSearchTerm}
                    />
                    <NestedCheckList items={clientLocationState} onChange={onCheck} />
                </div>
            </div>
            <div style={{ float: 'left', paddingLeft: 15, paddingTop: 8 }}>
                <SelectedModelsList
                    header={t('COMMON.LOCATIONS')}
                    selectedModels={locations.filter(
                        loc => selectedLocationIds.find(id => id === loc.locationId) !== undefined,
                    )}
                />
            </div>
        </div>
    );
};

export type SelectedModelsListProps = {
    header: string;
    selectedModels: ShareableLocation[];
};

export const SelectedModelsList: React.FunctionComponent<SelectedModelsListProps> = ({ header, selectedModels }) => {
    const selected = selectedModels.length;

    return (
        <div style={{ maxHeight: '200px', maxWidth: '250px', overflowY: 'auto' }}>
            <div className="branded-alternate">
                <h3 style={{ fontSize: 18 }}>{`${header} (${selected})`}</h3>
            </div>
            <ul style={{ listStyleType: 'disc' }}>
                {selectedModels.map(model => (
                    <li key={model.locationId}>{model.locationTitle}</li>
                ))}
            </ul>
        </div>
    );
};

export default MultipleLocationPicker;
